import {AnnotationEditorParamsType,AnnotationEditorType,AnnotationMode,getDocument,version,type PDFDocumentProxy} from 'pdfjs-dist';
import type {PDFViewer} from 'pdfjs-dist/web/pdf_viewer.mjs';
import {parsePdfInkAnnotations,type PdfInkAnnotation} from '@reados/contracts';
import {assertPdfInkVersion,inkFingerprint,inkFromPdfJs,inkToPdfJs} from './pdf-ink-codec';
import {PdfInkHistory} from './pdf-ink-history';
import {pdfDocumentOptions} from './pdf-decoders';
import {eraseInkPaths,type InkPoint} from './pdf-ink-eraser';

// These interfaces deliberately describe only the 5.7.284 seams used below.
// Keep this private surface in one module; do not expose PDF.js editor objects to React or storage.
interface Editor{
 id:string;pageIndex:number;annotationElementId:string|null;deleted:boolean;editorType:string;canAddComment:boolean;
 div:HTMLElement;isAttachedToDOM:boolean;serialize(copy?:boolean):unknown;remove():void;disableEditing():void;enableEditing():void;
}
interface Layer{
 pageIndex:number;div:HTMLElement;deserialize(data:unknown):Promise<Editor|null>;addOrRebuild(editor:Editor):void;
 startDrawingSession(event:{target:HTMLElement;offsetX:number;offsetY:number;pointerId:number;pointerType:string}):void;
 commitOrRemove():boolean;disable():void;updateMode(mode?:number):void;
 addUndoableEditor(editor:Editor):void;
}
interface Manager{
 currentLayer?:Layer;getLayer(index:number):Layer|undefined;getEditors(index:number):Iterable<Editor>;
 updateMode(mode:number,editId?:string|null,isFromUser?:boolean):Promise<void>;getMode():number;
 updateParams(type:number,value:unknown):void;commitOrRemove():void;unselectAll():void;undo():void;redo():void;delete():void;
 addCommands(command:{cmd:()=>void;undo:()=>void;post?:()=>void;type?:number;mustExec:boolean;overwriteIfSameType?:boolean;keepUndo?:boolean}):void;
 cleanUndoStack(type:number):void;
 addLayer(layer:Layer):void;
 addEditor(editor:Editor):void;
 rebuild(editor:Editor):void;
}
interface EventBus{on(name:string,listener:(event:any)=>void):void;off(name:string,listener:(event:any)=>void):void}
export interface PdfInkEditorState{canUndo:boolean;canRedo:boolean;hasSelection:boolean}
export interface PdfInkAdapterOptions{
 viewer:PDFViewer;document:PDFDocumentProxy;scrollElement:HTMLElement;
 onChange:(pageIndex:number,annotations:PdfInkAnnotation[])=>void;
 onState?:(state:PdfInkEditorState)=>void;onError?:(error:Error)=>void;
}
export type PdfInkMode='read'|'draw'|'erase'|'select';
interface InkContact{
 pointerId:number;layer:Layer;abort:AbortController;erase:boolean;previous:InkPoint|null;
 changes:Map<string,{before:Editor;after:Editor|null}>;
}
interface EraseSample{pageIndex:number;from:InkPoint;to:InkPoint;radius:number}

/** Version-bound bridge to the real upstream InkEditor. Persistence is owned by the caller. */
export class PdfInkAdapter{
 readonly ready:Promise<void>;
 private resolveReady!:()=>void;
 private rejectReady!:(error:Error)=>void;
 private manager:Manager|undefined;
 private readonly bus:EventBus;
 private readonly abort=new AbortController();
 private readonly pages=new Map<number,PdfInkAnnotation[]>();
 private readonly identities=new Map<string,string>();
 private readonly normalized=new Map<string,string>();
 private readonly installedPages=new Set<number>();
 private readonly pageTasks=new Map<number,Promise<void>>();
 private readonly listeners:Array<[string,(event:any)=>void]>=[];
 private readonly restores:Array<()=>void>=[];
 private mode:PdfInkMode='read';
 private color='#111827';
 private thickness=2;
 private disposed=false;
 private syncing=0;
 private pendingCollect=false;
 private penId:number|null=null;
 private contact:InkContact|null=null;
 private eraseQueue:Promise<void>=Promise.resolve();
 private readonly pendingErasures=new Set<EraseSample>();
 private readonly palmTouches=new Set<number>();
 private transition:Promise<void>=Promise.resolve();
 private state:PdfInkEditorState={canUndo:false,canRedo:false,hasSelection:false};
 private readonly history=new PdfInkHistory();
 private replaying=false;
 private readonly layerUndo=new WeakMap<Layer,(editor:Editor)=>void>();
 private readonly originalAnnotationMethods=new WeakMap<object,()=>unknown>();

 constructor(private readonly options:PdfInkAdapterOptions){
  assertPdfInkVersion(version);
  this.bus=options.viewer.eventBus as EventBus;
  this.ready=new Promise((resolve,reject)=>{this.resolveReady=resolve;this.rejectReady=reject;});
  // The owner may be in reading mode and never await ready directly.
  void this.ready.catch(()=>{});
  this.listen('annotationeditoruimanager',event=>this.connect(event.uiManager));
  this.listen('pagesinit',()=>{
   if(!this.manager&&!this.disposed){const error=new Error('此 PDF 的权限或格式不支持手写批注');this.rejectReady(error);this.fail(error);}
  });
  this.listen('annotationlayerrendered',event=>this.protectOriginalAnnotations(event.pageNumber-1));
  this.listen('annotationeditorlayerrendered',event=>{
   if(event.error){this.fail(event.error);return;}
   void this.restorePage(event.pageNumber-1).catch(error=>this.fail(error));
  });
  this.listen('editingstateschanged',event=>{
   const details=event.details;
   this.state={canUndo:this.history.canUndo,canRedo:this.history.canRedo,hasSelection:!!details.hasSelectedEditor};
   options.onState?.(this.state);this.scheduleCollect();
  });
  this.listen('annotationeditorparamschanged',()=>this.scheduleCollect());
  this.listen('switchannotationeditormode',()=>{ /* Native double click must not change the application mode. */ });
  const existing=(options.viewer as unknown as {_layerProperties?:{annotationEditorUIManager?:Manager}})._layerProperties?.annotationEditorUIManager;
  if(existing)this.connect(existing);
  const element=options.scrollElement,signal=this.abort.signal;
  element.addEventListener('pointerdown',this.pointerDown,{capture:true,signal});
  element.addEventListener('pointermove',this.pointerMove,{capture:true,signal});
  window.addEventListener('pointerup',this.pointerUp,{capture:true,signal});
  window.addEventListener('pointercancel',this.pointerUp,{capture:true,signal});
  window.addEventListener('blur',()=>this.finishContact(),{signal});
  document.addEventListener('visibilitychange',()=>{if(document.hidden)this.finishContact();},{signal});
  // Leave ordinary touch events intact: the browser owns single-finger scrolling,
  // and PdfViewer owns its existing two-finger zoom. Only an active pen blocks palms.
  for(const type of ['touchstart','touchmove','touchend','touchcancel'] as const)element.addEventListener(type,this.palmTouch,{capture:true,passive:false,signal});
  element.addEventListener('keyup',()=>this.scheduleCollect(),{signal});
  element.addEventListener('keydown',event=>{
   if(this.mode==='read'||event.altKey||!(event.ctrlKey||event.metaKey))return;
   const target=event.target instanceof HTMLElement?event.target:null;
   if(target?.closest('input,textarea,select')||target?.isContentEditable)return;
   const key=event.key.toLowerCase(),redo=key==='y'||key==='z'&&event.shiftKey;
   if(key!=='z'&&!redo)return;
   // Upstream KeyboardManager stores prototype callbacks, so it bypasses the
   // instance undo wrappers. Route shortcuts through the same contact/queue gate
   // as the toolbar, retaining one authoritative native history cursor.
   event.preventDefault();event.stopImmediatePropagation();
   if(redo)this.redo();else this.undo();
  },{capture:true,signal});
  element.addEventListener('focusout',()=>this.scheduleCollect(),{signal});
  // This surface promises Ink only. PDF.js also accepts image/text clipboard data;
  // do not display unsupported transient editors which the Ink sidecar cannot save.
  for(const type of ['paste','drop'] as const)element.addEventListener(type,event=>{
   if(this.mode!=='read'){event.preventDefault();event.stopImmediatePropagation();}
  },{capture:true,signal});
 }
 private listen(name:string,listener:(event:any)=>void){this.bus.on(name,listener);this.listeners.push([name,listener]);}
 private fail(error:unknown){if(!this.disposed)this.options.onError?.(error instanceof Error?error:new Error(String(error)));}
 private connect(manager:Manager){
  if(this.manager||this.disposed)return;
  if(!manager||typeof manager.getEditors!=='function'||typeof manager.getLayer!=='function'||typeof manager.updateMode!=='function'){
   const error=new Error('PDF 手写编辑器接口不可用');this.rejectReady(error);this.fail(error);return;
  }
  this.manager=manager;this.installHistory(manager);this.resolveReady();
  for(let index=0;index<this.options.document.numPages;index++)this.protectOriginalAnnotations(index);
 }
 private emitHistory(){this.state={...this.state,canUndo:this.history.canUndo,canRedo:this.history.canRedo};this.options.onState?.(this.state);}
 private installHistory(manager:Manager){
  const add=manager.addCommands,undo=manager.undo,redo=manager.redo,clean=manager.cleanUndoStack,addLayer=manager.addLayer,addEditor=manager.addEditor;
  if(typeof add!=='function'||typeof clean!=='function')throw new Error('PDF 手写撤销接口不可用');
  manager.addCommands=command=>{
   const epoch=this.history.epoch;
   const overwrite=!!command.overwriteIfSameType&&this.history.canOverwrite(command.type);
   add.call(manager,{...command,overwriteIfSameType:overwrite,cmd:()=>{if(epoch===this.history.epoch)command.cmd();},undo:()=>{if(epoch===this.history.epoch)command.undo();},post:command.post?()=>{if(epoch===this.history.epoch)command.post!();}:undefined});
   if(!this.replaying)this.history.add(command.type,overwrite);
   this.emitHistory();this.scheduleCollect();
  };
  manager.undo=()=>{
   if(!this.history.canUndo)return;this.replaying=true;
   try{undo.call(manager);this.history.undo();}finally{this.replaying=false;this.emitHistory();this.scheduleCollect();}
  };
  manager.redo=()=>{
   if(!this.history.canRedo)return;this.replaying=true;
   try{redo.call(manager);this.history.redo();}finally{this.replaying=false;this.emitHistory();this.scheduleCollect();}
  };
  manager.cleanUndoStack=type=>{clean.call(manager,type);this.history.clean(type);this.emitHistory();};
  const bindLayer=(layer:Layer)=>{
   if(this.layerUndo.has(layer))return;
   const original=layer.addUndoableEditor;this.layerUndo.set(layer,original);
   // Rehydration and virtual-page remounts are not user creation commands.
   layer.addUndoableEditor=editor=>{if(!this.identities.has(editor.id))original.call(layer,editor);};
  };
  manager.addLayer=layer=>{bindLayer(layer);addLayer.call(manager,layer);};
  manager.addEditor=editor=>{if(editor.editorType==='ink'&&!editor.annotationElementId)editor.canAddComment=false;addEditor.call(manager,editor);};
  for(let index=0;index<this.options.document.numPages;index++){const layer=manager.getLayer(index);if(layer)bindLayer(layer);}
  this.restores.push(()=>{
   manager.addCommands=add;manager.undo=undo;manager.redo=redo;manager.cleanUndoStack=clean;manager.addLayer=addLayer;manager.addEditor=addEditor;
   for(let index=0;index<this.options.document.numPages;index++){const layer=manager.getLayer(index),original=layer&&this.layerUndo.get(layer);if(layer&&original)layer.addUndoableEditor=original;}
  });
 }
 private protectOriginalAnnotations(pageIndex:number){
  const annotationLayer=(this.options.viewer.getPageView(pageIndex) as any)?.annotationLayer?.annotationLayer;
  if(!annotationLayer||this.originalAnnotationMethods.has(annotationLayer))return;
  const original=annotationLayer.getEditableAnnotations;
  if(typeof original!=='function'){this.fail(new Error('PDF 原始批注隔离接口不可用'));return;}
  // Keep the original PDF on the normal raster rendering path. Only sidecar-created
  // editors belong to this editor session; original annotations never become edits.
  annotationLayer.getEditableAnnotations=()=>[];
  this.originalAnnotationMethods.set(annotationLayer,original);
 }
 private pointerDown=(event:PointerEvent)=>{
  if(this.disposed)return;
  const target=event.target instanceof Element?event.target:null;
  if(event.pointerType==='touch'){
   // Stop PDF.js's pointer listener, not the browser's touch scroll/default action.
   if(this.mode!=='read')event.stopPropagation();
   if(this.penId!==null){event.preventDefault();event.stopImmediatePropagation();}
   return;
  }
  if(this.mode==='read')return;
  if(this.mode==='draw'||this.mode==='erase'){
   if(event.button!==0||event.ctrlKey||event.metaKey)return;
   if(this.contact){event.preventDefault();event.stopImmediatePropagation();return;}
   const page=target?.closest('.page'),pageIndex=Number(page?.getAttribute('data-page-number'))-1;
   const layer=Number.isInteger(pageIndex)&&pageIndex>=0?this.manager?.getLayer(pageIndex):undefined;
   if(!layer||!page?.contains(layer.div))return;
   event.preventDefault();event.stopImmediatePropagation();
   this.finishNativeStroke();
   this.manager?.updateParams(AnnotationEditorParamsType.INK_COLOR,this.color);
   this.manager?.updateParams(AnnotationEditorParamsType.INK_THICKNESS,this.thickness);
   const contact:InkContact={pointerId:event.pointerId,layer,abort:new AbortController(),erase:this.mode==='erase',previous:null,changes:new Map()};
   this.contact=contact;if(event.pointerType==='pen')this.penId=event.pointerId;
   const rect=layer.div.getBoundingClientRect();
   if(contact.erase)layer.div.focus({preventScroll:true});
   else layer.startDrawingSession({target:layer.div,offsetX:event.clientX-rect.left,offsetY:event.clientY-rect.top,pointerId:event.pointerId,pointerType:event.pointerType});
   // Register after startDrawingSession: PDF.js's window listener closes the last
   // sample first, then this listener commits this exact contact synchronously.
   // There is no timer from the previous stroke that can cut the next one short.
   const finish=(up:PointerEvent)=>{
    if(up.pointerId!==contact.pointerId||this.contact!==contact)return;
    if(contact.erase&&up.type==='pointerup')this.eraseAt(up,contact);
    this.finishContact(contact);
   };
   window.addEventListener('pointerup',finish,{signal:contact.abort.signal});
   window.addEventListener('pointercancel',finish,{signal:contact.abort.signal});
   layer.div.addEventListener('lostpointercapture',finish,{signal:contact.abort.signal});
   try{layer.div.setPointerCapture(event.pointerId);}catch{/* Synthetic test events have no active pointer. */}
   if(contact.erase)this.eraseAt(event,contact);
   return;
  }
  if(target?.closest('.annotationLayer')){event.stopPropagation();return;}
  if(this.mode==='select'&&!target?.closest('.inkEditor')){this.manager?.unselectAll();event.stopPropagation();return;}
  if(event.pointerType==='pen'){
   if(this.penId!==null&&this.penId!==event.pointerId){event.stopImmediatePropagation();return;}
   this.penId=event.pointerId;event.preventDefault();
  }
 };
 private pointerMove=(event:PointerEvent)=>{
  if(event.pointerType==='touch'&&this.mode!=='read')event.stopPropagation();
  if(this.contact?.erase&&event.pointerId===this.contact.pointerId){event.preventDefault();event.stopImmediatePropagation();this.eraseAt(event,this.contact);return;}
  if(event.pointerType==='pen'&&this.penId===event.pointerId)event.preventDefault();
 };
 private pointerUp=(event:PointerEvent)=>{
  if(event.pointerId===this.penId&&!this.contact)this.penId=null;
  const target=event.target instanceof Node?event.target:null;
  if(this.mode==='read'||(!target||!this.options.scrollElement.contains(target))&&event.pointerType!=='pen')return;
  if(this.mode==='select')this.scheduleCollect();
 };
 private finishNativeStroke(){
  this.manager?.currentLayer?.commitOrRemove();this.manager?.commitOrRemove();
  if(this.mode!=='select')this.manager?.unselectAll();
 }
 private finishContact(contact=this.contact){
  if(!contact||this.contact!==contact)return;
  this.contact=null;contact.abort.abort();if(this.penId===contact.pointerId)this.penId=null;
  try{if(contact.layer.div.hasPointerCapture(contact.pointerId))contact.layer.div.releasePointerCapture(contact.pointerId);}catch{/* The page may already have been removed. */}
  if(!contact.erase){contact.layer.commitOrRemove();this.finishNativeStroke();this.collect();return;}
  this.eraseQueue=this.eraseQueue.then(()=>{
   if(this.disposed||!contact.changes.size)return;
   const changes=[...contact.changes.values()];
   const apply=(undo:boolean)=>{
    this.syncing++;
    try{
     for(const change of changes)(undo?change.after:change.before)?.remove();
     for(const change of changes){const editor=undo?change.before:change.after;if(editor){this.normalized.delete(editor.id);this.manager!.rebuild(editor);}}
     this.manager!.unselectAll();
    }finally{this.syncing--;this.collect();}
   };
   this.manager!.addCommands({cmd:()=>apply(false),undo:()=>apply(true),mustExec:false});
   this.collect();
  }).catch(error=>this.fail(error));
 }
 private eraseAt(event:PointerEvent,contact:InkContact){
  // The editor layer uses dontFlip:true. Ink paths serialize in ordinary PDF
  // coordinates, so hit testing must use the page's normal (flipped) viewport.
  const {layer}=contact,rect=layer.div.getBoundingClientRect(),viewport=this.options.viewer.getPageView(layer.pageIndex).viewport;
  const point=viewport.convertToPdfPoint((event.clientX-rect.left)*viewport.width/rect.width,(event.clientY-rect.top)*viewport.height/rect.height) as [number,number];
  const from=contact.previous??point;contact.previous=point;
  const radius=7/Math.max(0.01,Math.hypot(viewport.transform[0],viewport.transform[1])*rect.width/viewport.width);
  const sample:EraseSample={pageIndex:layer.pageIndex,from,to:point,radius};this.pendingErasures.add(sample);
  this.eraseQueue=this.eraseQueue.then(async()=>{
   if(this.disposed){this.pendingErasures.delete(sample);return;}this.syncing++;
   try{
    for(const editor of [...this.manager!.getEditors(layer.pageIndex)]){
     const id=this.identities.get(editor.id);
     if(!id||editor.editorType!=='ink'||editor.annotationElementId||editor.deleted)continue;
     const original=inkFromPdfJs(editor.serialize(),id),remaining=eraseInkPaths(original,from,point,radius);
     if(remaining===original)continue;
     let replacement:Editor|null=null;
     if(remaining){replacement=await layer.deserialize(inkToPdfJs(remaining));if(!replacement)throw new Error('无法更新擦除后的笔迹');replacement.canAddComment=false;this.identities.set(replacement.id,id);}
     if(this.disposed){replacement?.remove();return;}
     editor.remove();if(replacement)layer.addOrRebuild(replacement);
     const existing=contact.changes.get(id);contact.changes.set(id,{before:existing?.before??editor,after:replacement});
    }
    this.manager!.unselectAll();
   }finally{this.pendingErasures.delete(sample);this.syncing--;if(!this.disposed)this.collect();}
  }).catch(error=>this.fail(error));
 }
 private palmTouch=(event:TouchEvent)=>{
  if(this.mode==='read')return;
  if(this.penId!==null)for(const touch of Array.from(event.touches))this.palmTouches.add(touch.identifier);
  const suppress=this.penId!==null||Array.from(event.changedTouches).some(touch=>this.palmTouches.has(touch.identifier));
  if(event.type==='touchend'||event.type==='touchcancel')for(const touch of Array.from(event.changedTouches))this.palmTouches.delete(touch.identifier);
  if(suppress){event.preventDefault();event.stopImmediatePropagation();}
 };
 private scheduleCollect(){
  if(this.pendingCollect||this.disposed)return;this.pendingCollect=true;
  queueMicrotask(()=>{this.pendingCollect=false;if(!this.disposed&&!this.syncing)try{this.collect();}catch(error){this.fail(error);}});
 }
 private collect(){
  if(!this.manager||this.syncing)return;
  for(const pageIndex of this.installedPages){
   const previous=this.pages.get(pageIndex)??[],previousById=new Map(previous.map(record=>[record.id,record])),next:PdfInkAnnotation[]=[];
   for(const editor of this.manager.getEditors(pageIndex)){
    if(editor.editorType!=='ink'||editor.annotationElementId||editor.deleted)continue;
    const raw=editor.serialize();if(!raw)continue;
    let id=this.identities.get(editor.id);if(!id){id=crypto.randomUUID();this.identities.set(editor.id,id);editor.canAddComment=false;}
    const current=inkFromPdfJs(raw,id),fingerprint=JSON.stringify(current),old=previousById.get(id);
    next.push(old&&this.normalized.get(editor.id)===fingerprint?old:current);
    this.normalized.set(editor.id,fingerprint);
   }
   parsePdfInkAnnotations(next,pageIndex);
   if(inkFingerprint(previous)!==inkFingerprint(next)){
    this.pages.set(pageIndex,next);this.options.onChange(pageIndex,structuredClone(next));
   }
  }
 }
 async hydrate(annotations:PdfInkAnnotation[]){
  const groups=new Map<number,PdfInkAnnotation[]>();
  for(const annotation of annotations){const group=groups.get(annotation.pageIndex)??[];group.push(annotation);groups.set(annotation.pageIndex,group);}
  for(const pageIndex of this.pages.keys())if(!groups.has(pageIndex))groups.set(pageIndex,[]);
  for(const [pageIndex,records] of groups)await this.hydratePage(pageIndex,records);
 }
 async hydratePage(pageIndex:number,annotations:PdfInkAnnotation[]){
  if(this.disposed)return;
  this.finishContact();await this.eraseQueue;
  if(pageIndex<0||pageIndex>=this.options.document.numPages)throw new Error('手写批注页码超出文档范围');
  const valid=parsePdfInkAnnotations(annotations,pageIndex);
  if(inkFingerprint(valid)===inkFingerprint(this.pages.get(pageIndex)??[])){await this.restorePage(pageIndex);return;}
  await this.pageTasks.get(pageIndex);
  this.syncing++;
  try{
   if(this.installedPages.has(pageIndex)&&this.manager){
    this.history.invalidate();this.emitHistory();
    for(const editor of [...this.manager.getEditors(pageIndex)])if(this.identities.has(editor.id))editor.remove();
    this.installedPages.delete(pageIndex);
   }
   this.pages.set(pageIndex,valid);
  }finally{this.syncing--;}
  await this.restorePage(pageIndex);
 }
 private restorePage(pageIndex:number):Promise<void>{
  const pending=this.pageTasks.get(pageIndex);if(pending)return pending;
  const layer=this.manager?.getLayer(pageIndex);
  if(!layer||this.disposed)return Promise.resolve();
  // A layer may be evicted and recreated while its Editors remain in UIManager.
  if(this.installedPages.has(pageIndex))return Promise.resolve();
  const task=(async()=>{
   this.syncing++;
   try{
    this.protectOriginalAnnotations(pageIndex);
    for(const annotation of this.pages.get(pageIndex)??[]){
     const editor=await layer.deserialize(inkToPdfJs(annotation));
     if(this.disposed)return;
     if(!editor)throw new Error('无法恢复 PDF 手写批注');
     editor.canAddComment=false;this.identities.set(editor.id,annotation.id);layer.addOrRebuild(editor);
     this.normalized.set(editor.id,JSON.stringify(inkFromPdfJs(editor.serialize(),annotation.id)));
    }
    this.installedPages.add(pageIndex);
    if(this.mode==='read')layer.disable();else layer.updateMode(AnnotationEditorType.INK);
   }finally{this.syncing--;}
  })();
  this.pageTasks.set(pageIndex,task);
  void task.finally(()=>{if(this.pageTasks.get(pageIndex)===task)this.pageTasks.delete(pageIndex);}).catch(()=>{});
  return task;
 }
 setMode(mode:PdfInkMode):Promise<void>{
  this.transition=this.transition.catch(()=>{}).then(async()=>{
   await this.ready;if(this.disposed)return;await this.commit();
   this.mode=mode;
   // Do not call viewer.annotationEditorMode: it removes original editable PDF
   // annotations from the raster. We only enable our independent Ink sidecar.
   await this.manager!.updateMode(mode==='read'?AnnotationEditorType.NONE:AnnotationEditorType.INK,null,true);
   this.manager!.unselectAll();
   this.manager!.updateParams(AnnotationEditorParamsType.INK_COLOR,this.color);
   this.manager!.updateParams(AnnotationEditorParamsType.INK_THICKNESS,this.thickness);
   this.options.scrollElement.dataset.inkMode=mode;
  });
  return this.transition;
 }
 setWriting(writing:boolean){return this.setMode(writing?'draw':'read');}
 setColor(color:string){
  if(!/^#[0-9a-f]{6}$/i.test(color))throw new Error('手写颜色无效');
  this.color=color;this.manager?.updateParams(AnnotationEditorParamsType.INK_COLOR,color);this.scheduleCollect();
 }
 setThickness(thickness:number){
  if(!Number.isFinite(thickness)||thickness<=0||thickness>100)throw new Error('手写笔宽无效');
  this.thickness=thickness;this.manager?.updateParams(AnnotationEditorParamsType.INK_THICKNESS,thickness);this.scheduleCollect();
 }
 undo(){this.finishContact();void this.eraseQueue.then(()=>{this.finishNativeStroke();this.manager?.undo();this.scheduleCollect();});}
 redo(){this.finishContact();void this.eraseQueue.then(()=>{this.manager?.redo();this.scheduleCollect();});}
 deleteSelection(){this.manager?.delete();this.scheduleCollect();}
 async commit(){
  if(this.disposed)return;
  this.finishContact();await this.eraseQueue;this.finishNativeStroke();
  await Promise.all(this.pageTasks.values());this.collect();
 }
 async exportPdf():Promise<Uint8Array>{
  await this.commit();
  const records=[...this.pages.values()].flat().map(annotation=>structuredClone(annotation));
  // A fresh document avoids leaking unrelated form edits or transient editor IDs
  // and includes pages which have never been rendered by the virtualized viewer.
  const {wasmUrl}=pdfDocumentOptions('');
  const task=getDocument({data:await this.options.document.getData(),wasmUrl});
  try{
   const snapshot=await task.promise;
   if(records.length===0)return await snapshot.getData();
   records.forEach((record,index)=>snapshot.annotationStorage.setValue(`pdfjs_internal_editor_${index}`,inkToPdfJs(record)));
   return await snapshot.saveDocument();
  }finally{await task.destroy();}
 }
 async capturePage(pageIndex:number):Promise<Blob>{
  if(!Number.isInteger(pageIndex)||pageIndex<0||pageIndex>=this.options.document.numPages)throw new Error('截图页码无效');
  const {wasmUrl}=pdfDocumentOptions('');
  const task=getDocument({data:await this.exportPdf(),wasmUrl});
  try{
   const snapshot=await task.promise,page=await snapshot.getPage(pageIndex+1),base=page.getViewport({scale:1});
   const scale=Math.min(2,Math.sqrt(8_388_608/(base.width*base.height))),viewport=page.getViewport({scale});
   const canvas=document.createElement('canvas');canvas.width=Math.ceil(viewport.width);canvas.height=Math.ceil(viewport.height);
   try{
    await page.render({canvas,viewport,annotationMode:AnnotationMode.ENABLE}).promise;
    return await new Promise<Blob>((resolve,reject)=>canvas.toBlob(blob=>blob?resolve(blob):reject(new Error('含手写批注的截图生成失败')),'image/png'));
   }finally{canvas.width=canvas.height=0;}
  }finally{await task.destroy();}
 }
 destroy(){
  if(this.disposed)return;
  // React cleanup cannot await; finish the in-memory stroke and notify the owner
  // before detaching. The session owner retains responsibility for async persistence.
  try{
   this.finishContact();this.finishNativeStroke();this.collect();
   // Rebuilding a legacy multi-path InkEditor has an upstream async seam. If the
   // reader closes in that microtask, preserve every already received erase
   // sample in the durable sidecar before detaching; no live editor is required.
   const changedPages=new Set<number>();
   for(const sample of this.pendingErasures){
    const previous=this.pages.get(sample.pageIndex)??[],next=previous.flatMap(annotation=>{const remaining=eraseInkPaths(annotation,sample.from,sample.to,sample.radius);return remaining?[remaining]:[];});
    if(inkFingerprint(previous)!==inkFingerprint(next)){this.pages.set(sample.pageIndex,next);changedPages.add(sample.pageIndex);}
   }
   for(const pageIndex of changedPages)this.options.onChange(pageIndex,structuredClone(this.pages.get(pageIndex)!));
  }catch(error){this.fail(error);}
  this.disposed=true;this.resolveReady();this.abort.abort();
  for(const [name,listener] of this.listeners)this.bus.off(name,listener);
  for(const restore of this.restores)restore();
  for(let index=0;index<this.options.document.numPages;index++){
   const layer=(this.options.viewer.getPageView(index) as any)?.annotationLayer?.annotationLayer,original=layer&&this.originalAnnotationMethods.get(layer);
   if(layer&&original)layer.getEditableAnnotations=original;
  }
  delete this.options.scrollElement.dataset.inkMode;
 }
}
